export interface Transaction {
  id?: number;
  name: string;
  money: number;
  fee: number;
  currency: string;
  account: string;
  executor: string;
  transaction_time: string;
  destination_time?: string;
  category: string;
  payment: string;
  note?: string;
}

export interface TransactionFilters {
  start?: string;
  end?: string;
  name?: string;
  category?: string;
  account?: string;
  executor?: string;
  currency?: string;
  page?: number;
  limit?: number;
  sort?: 'asc' | 'desc';
}

export interface TransactionListResponse {
  transactions: Transaction[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    totalMoney: number;
    totalFee: number;
    netTotal: number;
  };
}

export interface MetaConfig {
  names: string[];
  categories: string[];
  accounts: string[];
  executors: string[];
  currencies: string[];
}

class ApiClient {
  private baseUrl = '/api';

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Development mode fallback
    if (import.meta.env.DEV && !window.Telegram?.WebApp?.initData) {
      headers['x-dev-mode'] = 'true';
    } else if (window.Telegram?.WebApp?.initData) {
      headers['x-telegram-init-data'] = window.Telegram.WebApp.initData;
    }

    return headers;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    // Для статуса 204 (No Content) не пытаемся парсить JSON
    if (response.status === 204) {
      return undefined as T;
    }

    // Проверяем, есть ли контент для парсинга
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    // Если контента нет или он не JSON, возвращаем undefined
    return undefined as T;
  }

  async getMeta(): Promise<MetaConfig> {
    return this.request<MetaConfig>('/meta');
  }

  async getTransactions(filters: TransactionFilters = {}): Promise<TransactionListResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const queryString = params.toString();
    return this.request<TransactionListResponse>(`/transactions${queryString ? `?${queryString}` : ''}`);
  }

  async createTransaction(transaction: Omit<Transaction, 'id'>): Promise<Transaction> {
    return this.request<Transaction>('/transactions', {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
  }

  async updateTransaction(id: number, transaction: Omit<Transaction, 'id'>): Promise<Transaction> {
    return this.request<Transaction>(`/transactions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(transaction),
    });
  }

  async patchTransaction(id: number, updates: Partial<Omit<Transaction, 'id'>>): Promise<Transaction> {
    return this.request<Transaction>(`/transactions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteTransaction(id: number): Promise<void> {
    await this.request<void>(`/transactions/${id}`, {
      method: 'DELETE',
    });
  }
}

export const apiClient = new ApiClient();